/**
 * Design tokens del tablero hi-fi "Colectivo Moda", tomados verbatim del
 * design handoff (`tablero/design_handoff_tablero_colectivo_moda/`). Fuente
 * de verdad para colores/tipografía usados por `Header`, `Combo` y las
 * vistas Global / Mi tienda (Tasks 5-10).
 *
 * NO usar colores "a ojo" en componentes nuevos: agregar el token aquí si
 * falta uno, para que toda la UI del tablero siga usando la misma paleta.
 */

export const fontFamily = "'Manrope', system-ui, -apple-system, sans-serif";

/** Paleta principal (fondo, tarjetas, bordes, marca y acentos). */
export const colors = {
  bg: '#edeff3',
  card: '#fff',
  border: '#e7e9ef',
  borderAccent: '#dfe4f2',

  brand: '#22349b',
  brandHover: '#1a2878',
  brandSoftBg: '#eef1fb',
  brandSoftBorder: '#dbe1f6',

  naranja: '#dd6a24',
  verde: '#17a44d',
  rojo: '#d63a33',
  azulClaro: '#1f97dd',
  dorado: '#9a7c1b',

  /** Escala de texto (de más a menos contraste). */
  texto: {
    primary: '#14171d',
    strong: '#20242d',
    secondary: '#5b616d',
    tertiary: '#868c97',
    quaternary: '#9298a3',
    muted: '#9aa0ab',
    body: '#3a4150',
    coaching: '#2b3350',
  },

  /** Fondos y bordes secundarios reutilizados en tarjetas/tablas. */
  ringTrack: '#eef0f4',
  rowBorder: '#f0f2f6',
  rowHover: '#f5f7fb',
  panelBg: '#f4f6fa',
  chipBg: '#f6f7fa',
  scrollThumb: '#cdd3de',
  chevron: '#ccd1db',
  iconMuted: '#aeb4bf',
  consultasBar: '#a9b2c2',
  /** Relleno del área bajo la línea "Consultas" del gráfico por hora (Mi tienda). */
  areaFill: '#dfe4ee',

  /** Switch segmentado del Header. */
  switchBg: '#eef0f5',
  switchBorder: '#e2e5ee',

  /** Pill "En vivo" (Mi tienda). */
  liveBg: '#eafaf0',
  liveBorder: '#bfe9cf',
  liveText: '#0f7a3b',

  /** Estatus de "Productos más negados". */
  estatusFueraCatalogoBg: '#fdeceb',
  estatusFueraCatalogoText: '#c62f28',
  estatusBotaderoBg: '#fdf5e3',
  estatusBotaderoText: '#8a6d12',
  estatusAgotadoBg: '#eef0f4',
  estatusAgotadoText: '#5b616d',

  /** Nota de pie de "Detalle operativo por sucursal" ("Mostrando 40 de N"). */
  footerBg: '#fbfcfe',

  /** Placeholder de foto en "Productos más negados" (aún sin imagen real). */
  photoPlaceholderBg: '#f2f4f8',
  photoPlaceholderIcon: '#c2c8d3',

  /** Umbral intermedio de tasa/SLA (ni verde ni rojo). */
  umbralMedio: '#b7891f',
  umbralMedioAlt: '#c98a12',

  /** Tarjeta de coaching / insight de vendedores. */
  coachingBg: '#eef1fb',
  coachingBorder: '#d6ddf4',
  rankOro: '#e6cf86',

  /**
   * Colores del modal drill "Detalle de producto negado" (rediseño 2026-07,
   * spec del usuario §5) — jerarquía deliberada: como TODO el dato ahí es
   * "negado", el rojo fuerte (`negadoCritico`/`negadoTexto`) se reserva para
   * lo crítico (talla más negada, día pico) y el resto queda en tono apagado
   * (`negadoSuave`), para que lo importante resalte sin gritar en cada barra.
   * `marca` es el fondo del header del modal (NO reusa `colors.brand`, que es
   * un azul distinto usado en el resto del tablero). Idéntico en
   * `cdr-sa-admin` (Task 2, espejo) — no cambiar un repo sin el otro.
   */
  drill: {
    negadoCritico: '#D23B2D',
    negadoSuave: '#EA9A96',
    negadoTexto: '#C63A2D',
    marca: '#2A3690',
  },

  /**
   * Colores semánticos de la tarjeta de vendedor (embudo Actividad + barra
   * Contado/Crédito + aro de efectividad) — un color, un significado. NO
   * reusar el azul para "surtió" (antes colisionaba con "pares").
   */
  serie: {
    surtio: '#639922',
    negado: '#E24B4A',
    pendiente: '#B4B2A9',
    contado: '#378ADD',
    credito: '#7F77DD',
    aroMedio: '#BA7517',
  },
} as const;

/**
 * Colores de avatar para las tarjetas de "Vendedores del día" (se asignan
 * por índice, ciclando si hay más vendedores que colores).
 */
export const avatarColors = [
  '#22349b',
  '#dd6a24',
  '#17a44d',
  '#1f97dd',
  '#7a4fd0',
  '#d63a68',
  '#0e8f9c',
] as const;

/**
 * Keyframes globales del handoff. Se inyectan una sola vez (vía `<style>`)
 * desde `IndicadoresScreen`, ya que las reglas `@keyframes` son globales al
 * documento sin importar en qué nodo del árbol se declaren.
 */
export const keyframesCss = `
@keyframes cmpulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.3;transform:scale(.75)}}
@keyframes cmfade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
`;
